﻿import {
  AuditOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CompassOutlined,
  DatabaseOutlined,
  FlagOutlined,
  GiftOutlined,
  LineChartOutlined,
  LogoutOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  ShoppingOutlined,
  TeamOutlined,
  UserOutlined
} from "@ant-design/icons";
import { ProCard, StatisticCard } from "@ant-design/pro-card";
import {
  App as AntdApp,
  Avatar,
  Badge,
  Button,
  ConfigProvider,
  DatePicker,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  Layout,
  Menu,
  Modal,
  Progress,
  Result,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  theme,
  type MenuProps,
  type TableColumnsType
} from "antd";
import zhCN from "antd/locale/zh_CN";
import dayjs, { type Dayjs } from "dayjs";
import {
  lazy,
  startTransition,
  Suspense,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState
} from "react";
import navigationLogo from "./assets/navigation-logo-ai-transparent-256.png";
import { AccountSettings } from "./components/AccountSettings";
import { ConnectionStatus } from "./components/ConnectionStatus";
import { LoginPage } from "./components/LoginPage";
import { Funnel, SourceDonut, TrendChart, sourceMeta } from "./components/Visuals";
import {
  getBindingHistory,
  getDashboard,
  getToken,
  me,
  rebindPrimary,
  setToken
} from "./services/api";
import type { SessionUser, SourceDashboard, SourcePlayer, SourceType } from "./types";

const OperationsPanel = lazy(() => import("./components/OperationsPanel").then((module) => ({
  default: module.OperationsPanel
})));
const CommercePanel = lazy(() => import("./components/CommercePanel").then((module) => ({
  default: module.CommercePanel
})));
const OnboardingPanel = lazy(() => import("./components/OnboardingPanel").then((module) => ({
  default: module.OnboardingPanel
})));

type PageKey = "source" | "onboarding" | "commerce" | "campaigns" | "risks" | "reputation" | "rewards" | "audit";
type RebindForm = {
  playerId: number;
  uuid: string;
  name: string;
  reason: string;
  totp: string;
};

const sourceLabels: Record<SourceType, string> = {
  PLAYER_REFERRAL: "玩家推荐",
  BILIBILI: "B站外招",
  DOUYIN: "抖音外招",
  LEGACY: "历史来源"
};

const journeyLabels: Record<string, string> = {
  PENDING_WHITELIST: "等待白名单",
  WHITELISTED: "已加入白名单",
  ACTIVE: "考察中",
  QUALIFIED: "已生效",
  FROZEN: "风险冻结",
  REJECTED: "已拒绝"
};

const menuItems: MenuProps["items"] = [
  {
    key: "overview",
    label: "数据总览",
    type: "group",
    children: [
      { key: "source", label: "玩家来源分析", icon: <BarChartOutlined /> }
    ]
  },
  {
    key: "invitation",
    label: "邀请运营",
    type: "group",
    children: [
      { key: "onboarding", label: "新手接待与礼包", icon: <GiftOutlined /> },
      { key: "campaigns", label: "渠道活动码", icon: <FlagOutlined /> },
      { key: "risks", label: "风险审核", icon: <SafetyCertificateOutlined /> }
    ]
  },
  {
    key: "governance",
    label: "玩家治理",
    type: "group",
    children: [
      { key: "commerce", label: "信誉分商城", icon: <ShoppingOutlined /> },
      { key: "reputation", label: "信誉流水", icon: <LineChartOutlined /> },
      { key: "rewards", label: "奖励补偿", icon: <GiftOutlined /> },
      { key: "audit", label: "审计日志", icon: <AuditOutlined /> }
    ]
  }
];

function RootApp() {
  const { message } = AntdApp.useApp();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [authChecking, setAuthChecking] = useState(Boolean(getToken()));
  const [page, setPage] = useState<PageKey>("source");
  const [dashboard, setDashboard] = useState<SourceDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [source, setSource] = useState<SourceType | undefined>();
  const [journeyStatus, setJourneyStatus] = useState<string | undefined>();
  const [riskStatus, setRiskStatus] = useState<string | undefined>();
  const [campaign, setCampaign] = useState("");
  const [inviter, setInviter] = useState("");
  const [selected, setSelected] = useState<SourcePlayer | null>(null);
  const [bindingHistory, setBindingHistory] = useState<Array<Record<string, unknown>>>([]);
  const [rebindOpen, setRebindOpen] = useState(false);
  const [rebindLoading, setRebindLoading] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [playerPage, setPlayerPage] = useState(1);
  const [playerPageSize, setPlayerPageSize] = useState(20);
  const [form] = Form.useForm<RebindForm>();
  const deferredDashboard = useDeferredValue(dashboard);

  useEffect(() => {
    if (!getToken()) {
      setAuthChecking(false);
      return;
    }
    const controller = new AbortController();
    me(controller.signal)
      .then((session) => setUser(session.user))
      .catch((error) => {
        if ((error as Error).name !== "AbortError") {
          setToken(null);
        }
      })
      .finally(() => setAuthChecking(false));
    return () => controller.abort();
  }, []);

  const loadDashboard = useCallback(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    getDashboard({
      start: range?.[0].startOf("day").toISOString(),
      end: range?.[1].endOf("day").toISOString(),
      source,
      status: journeyStatus,
      riskStatus,
      campaign: campaign.trim() || undefined,
      inviter: inviter.trim() || undefined,
      current: playerPage,
      pageSize: playerPageSize
    }, controller.signal)
      .then((result) => startTransition(() => setDashboard(result)))
      .catch((error) => {
        if ((error as Error).name !== "AbortError") {
          setLoadError(error instanceof Error ? error.message : "真实来源数据加载失败");
        }
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [
    campaign,
    inviter,
    journeyStatus,
    playerPage,
    playerPageSize,
    range,
    riskStatus,
    source
  ]);

  useEffect(() => {
    if (user && page === "source") {
      return loadDashboard();
    }
  }, [loadDashboard, page, user]);

  useEffect(() => {
    if (!selected) {
      setBindingHistory([]);
      return;
    }
    const controller = new AbortController();
    getBindingHistory(selected.id, controller.signal)
      .then((result) => setBindingHistory(result.history))
      .catch(() => setBindingHistory([]));
    return () => controller.abort();
  }, [selected]);

  const columns = useMemo<TableColumnsType<SourcePlayer>>(() => [
    {
      title: "主账号",
      dataIndex: "playerName",
      fixed: "left",
      width: 160,
      render: (_, row) => (
        <button className="player-link" onClick={() => setSelected(row)}>
          <Avatar size={28}>{row.playerName.slice(0, 1).toUpperCase()}</Avatar>
          <span>{row.playerName}</span>
        </button>
      )
    },
    { title: "QQ", dataIndex: "qqMasked", width: 120 },
    {
      title: "来源",
      dataIndex: "source",
      width: 120,
      render: (_, row) => (
        <Tag color={sourceMeta[row.source].color}>{sourceLabels[row.source]}</Tag>
      )
    },
    { title: "活动 / 邀请人", width: 170, render: (_, row) => row.campaign ?? row.inviter ?? "—" },
    {
      title: "信誉",
      dataIndex: "reputation",
      width: 140,
      sorter: (a, b) => a.reputation - b.reputation,
      render: (_, row) => (
        <div className="score-cell">
          <b>{row.reputation}</b>
          <Progress
            percent={row.reputation}
            showInfo={false}
            size="small"
            strokeColor={row.reputation <= 80 ? "#ff4d4f" : row.reputation <= 90 ? "#faad14" : "#52c41a"}
          />
        </div>
      )
    },
    {
      title: "旅程状态",
      dataIndex: "status",
      width: 135,
      render: (_, row) => (
        <Tag color={row.status === "QUALIFIED" ? "success" : row.status === "FROZEN" ? "warning" : "processing"}>
          {journeyLabels[row.status] ?? row.status}
        </Tag>
      )
    },
    {
      title: "累计在线",
      dataIndex: "cumulativeSeconds",
      width: 110,
      render: (_, row) => `${(row.cumulativeSeconds / 3600).toFixed(1)}h`
    },
    {
      title: "申请时间",
      dataIndex: "createdAt",
      width: 180,
      render: (value: string) => dayjs(value).format("YYYY-MM-DD HH:mm:ss")
    }
  ], []);

  if (authChecking) {
    return <div className="full-spin"><Spin size="large" /></div>;
  }
  if (!user) {
    return <LoginPage onLogin={setUser} />;
  }

  const summary = deferredDashboard?.summary;
  const accountMenu: MenuProps = {
    items: [
      {
        key: "account",
        icon: <UserOutlined />,
        label: "账号与安全"
      },
      { type: "divider" },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: "退出登录"
      }
    ],
    onClick: ({ key }) => {
      if (key === "account") {
        setAccountOpen(true);
      }
      if (key === "logout") {
        setToken(null);
        setUser(null);
      }
    }
  };

  return (
    <Layout className="admin-shell" hasSider>
      <Layout.Sider className="admin-sider" width={232} theme="light">
        <div className="admin-brand">
          <img src={navigationLogo} alt="领航计划" />
          <div>
            <b>领航计划</b>
            <span>NAVIGATION PLAN</span>
          </div>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[page]}
          items={menuItems}
          onClick={({ key }) => setPage(key as PageKey)}
        />
        <div className="admin-sider__status">
          <DatabaseOutlined />
          <span>MySQL 真实账本</span>
        </div>
      </Layout.Sider>
      <Layout className="admin-main">
        <Layout.Header className="admin-header">
          <ConnectionStatus />
          <Dropdown menu={accountMenu}>
            <button type="button" className="admin-operator">
              <Avatar size={30} src={user.qqAvatarUrl ?? undefined}>
                {user.username.slice(0, 1).toUpperCase()}
              </Avatar>
              <span>{user.username}</span>
            </button>
          </Dropdown>
        </Layout.Header>
        <Layout.Content className="admin-content">
          {page === "source" ? (
            <main className="page-container">
              <header className="page-header">
                <div>
                  <Space size={10} align="center">
                    <h1>玩家来源分析</h1>
                    <Tag color="blue">真实领航账本</Tag>
                  </Space>
                  <p>查看申请、白名单、首次进服、有效邀请与玩家来源成本。</p>
                </div>
                {
            <Button
              key="refresh"
              type="primary"
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={loadDashboard}
            >
              刷新数据
            </Button>
                }
              </header>
          <ProCard className="filter-card" bordered>
            <Space wrap size={[12, 12]}>
              <DatePicker.RangePicker
                value={range}
                onChange={(value) => setRange(value as [Dayjs, Dayjs] | null)}
                allowClear
              />
              <Select<SourceType>
                allowClear
                placeholder="全部来源"
                value={source}
                onChange={setSource}
                options={Object.entries(sourceLabels).map(([value, label]) => ({ value, label }))}
                style={{ width: 140 }}
              />
              <Select
                allowClear
                placeholder="旅程状态"
                value={journeyStatus}
                onChange={setJourneyStatus}
                options={Object.entries(journeyLabels).map(([value, label]) => ({ value, label }))}
                style={{ width: 150 }}
              />
              <Select
                allowClear
                placeholder="风险状态"
                value={riskStatus}
                onChange={setRiskStatus}
                options={[
                  { value: "NONE", label: "无风险" },
                  { value: "OPEN", label: "待审核" },
                  { value: "CLEARED", label: "已解除" },
                  { value: "CONFIRMED", label: "已确认" }
                ]}
                style={{ width: 130 }}
              />
              <Input
                allowClear
                value={campaign}
                onChange={(event) => setCampaign(event.target.value)}
                placeholder="活动名称"
                style={{ width: 140 }}
              />
              <Input
                allowClear
                value={inviter}
                onChange={(event) => setInviter(event.target.value)}
                placeholder="邀请人"
                style={{ width: 130 }}
              />
              <Button icon={<ReloadOutlined />} loading={loading} onClick={loadDashboard}>应用筛选</Button>
            </Space>
          </ProCard>

          {loadError ? (
            <ProCard className="content-card" bordered>
              <Result
                status="error"
                title="真实数据源连接失败"
                subTitle={loadError}
                extra={<Button type="primary" onClick={loadDashboard}>重新连接</Button>}
              />
            </ProCard>
          ) : summary ? (
            <>
              <section className="metric-grid">
                <StatisticCard
                  statistic={{ title: "申请总数", value: summary.total, suffix: "人", icon: <TeamOutlined /> }}
                />
                <StatisticCard
                  statistic={{
                    title: "有效邀请",
                    value: summary.qualified,
                    suffix: "人",
                    description: `${(summary.conversionRate * 100).toFixed(1)}% 总转化`
                  }}
                />
                <StatisticCard
                  statistic={{
                    title: "风险冻结",
                    value: summary.frozen,
                    suffix: "人",
                    status: summary.frozen ? "warning" : "success"
                  }}
                />
                <StatisticCard
                  statistic={{
                    title: "已发金币",
                    value: summary.moneyCost,
                    suffix: "金币",
                    description: `${summary.pointsCost} 点券 · ${summary.flightGranted} 份飞行`
                  }}
                />
              </section>

              <section className="dashboard-grid">
                <ProCard className="analysis-card" title="来源构成" bordered>
                  <SourceDonut rows={deferredDashboard?.sourceCounts ?? []} />
                </ProCard>
                <ProCard
                  className="analysis-card"
                  title={deferredDashboard?.trendPeriod === "FILTERED"
                    ? "筛选周期每日新增与生效"
                    : "近 7 日新增与生效"}
                  bordered
                >
                  <TrendChart
                    rows={deferredDashboard?.trend ?? []}
                    emptyDescription={deferredDashboard?.trendPeriod === "FILTERED"
                      ? "筛选周期暂无真实变化"
                      : "近 7 日暂无真实变化"}
                  />
                </ProCard>
                <ProCard className="analysis-card" title="转化漏斗" bordered>
                  <Funnel rows={deferredDashboard?.funnel ?? []} />
                </ProCard>
                <ProCard className="analysis-card" title="信誉规则" bordered>
                  <div className="reputation-scale">
                    <div><b>100</b><span>新 QQ 社区身份初始信誉</span></div>
                    <div><b>&lt; 100</b><span>正向获得信誉分按一半向上取整</span></div>
                    <div><b>&lt; 80 / &lt; 70</b><span>仅主 QQ 群禁言 / 加游戏封禁；不冻结邀请码</span></div>
                  </div>
                </ProCard>
              </section>

              <ProCard
                className="content-card"
                title="玩家来源明细"
                extra={(
                  <Space size={12}>
                    <span className="data-provenance">
                      MySQL · {deferredDashboard
                        ? dayjs(deferredDashboard.generatedAt).format("HH:mm:ss")
                        : "读取中"}
                    </span>
                    <Badge
                      status="processing"
                      text={`共 ${deferredDashboard?.playersTotal ?? 0} 条真实记录`}
                    />
                  </Space>
                )}
                bordered
              >
                <Table<SourcePlayer>
                  rowKey="id"
                  columns={columns}
                  dataSource={deferredDashboard?.players ?? []}
                  loading={loading}
                  virtual
                  scroll={{ x: 1140, y: 520 }}
                  pagination={{
                    current: playerPage,
                    pageSize: playerPageSize,
                    total: deferredDashboard?.playersTotal ?? 0,
                    showSizeChanger: true,
                    showTotal: (total) => `数据库共 ${total} 条`,
                    onChange: (current, pageSize) => {
                      setPlayerPage(current);
                      setPlayerPageSize(pageSize);
                    }
                  }}
                  locale={{ emptyText: <Empty description="当前账本暂无真实玩家数据" /> }}
                />
              </ProCard>
            </>
          ) : (
            <div className="dashboard-empty"><Spin size="large" description="正在读取真实账本" /></div>
          )}
            </main>
          ) : (
            <main className="page-container">
              <Suspense fallback={<div className="dashboard-empty"><Spin size="large" /></div>}>
                {page === "commerce" ? (
                  <CommercePanel user={user} />
                ) : page === "onboarding" ? (
                  <OnboardingPanel user={user} />
                ) : (
                  <OperationsPanel
                    page={page}
                    canOperate={user.role === "OWNER" || user.role === "OPERATOR"}
                  />
                )}
              </Suspense>
            </main>
          )}

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title="玩家来源详情"
        size="large"
      >
        {selected && (
          <div className="player-drawer">
            <div className="player-drawer__identity">
              <Avatar size={54}>{selected.playerName.slice(0, 1)}</Avatar>
              <div><h2>{selected.playerName}</h2><code>{selected.playerUuid}</code></div>
              <Tag color={selected.reputation > 90 ? "success" : "warning"}>{selected.reputation} 分</Tag>
            </div>
            <dl>
              <div><dt>QQ</dt><dd>{selected.qqMasked}（默认脱敏）</dd></div>
              <div><dt>来源</dt><dd>{sourceLabels[selected.source]}</dd></div>
              <div><dt>活动 / 邀请人</dt><dd>{selected.campaign ?? selected.inviter ?? "—"}</dd></div>
              <div><dt>申请状态</dt><dd>{journeyLabels[selected.status] ?? selected.status}</dd></div>
              <div><dt>风险</dt><dd>{selected.riskStatus}</dd></div>
              <div><dt>累计在线</dt><dd>{(selected.cumulativeSeconds / 3600).toFixed(2)} 小时</dd></div>
              <div><dt>首次进服</dt><dd>{selected.firstJoinAt ? dayjs(selected.firstJoinAt).format("YYYY-MM-DD HH:mm") : "尚未进服"}</dd></div>
              <div><dt>有效结算</dt><dd>{selected.qualifiedAt ? dayjs(selected.qualifiedAt).format("YYYY-MM-DD HH:mm") : "尚未满足"}</dd></div>
            </dl>
            <h3>主账号换绑历史</h3>
            {bindingHistory.length ? (
              <Timeline
                items={bindingHistory.map((entry) => ({
                  color: "blue",
                  children: (
                    <div className="binding-history">
                      <b>{String(entry.oldPlayerName)} → {String(entry.newPlayerName)}</b>
                      <span>{String(entry.reason)}</span>
                      <small>{dayjs(String(entry.createdAt)).format("YYYY-MM-DD HH:mm")}</small>
                    </div>
                  )
                }))}
              />
            ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无人工换绑记录" />}
            {(user.role === "OWNER" || user.role === "OPERATOR") && (
              <Button type="primary" icon={<CompassOutlined />} onClick={() => setRebindOpen(true)}>
                更换领航主账号
              </Button>
            )}
          </div>
        )}
      </Drawer>

      <Modal
        title="更换领航主账号"
        open={rebindOpen}
        confirmLoading={rebindLoading}
        okText="确认换绑"
        cancelText="取消"
        destroyOnHidden
        onCancel={() => setRebindOpen(false)}
        onOk={() => form.submit()}
      >
        <p className="modal-note">
          历史邀请、已发奖励和已开始的 fly 不迁移；未结算礼包改投新账号，邀请码保持不变。
        </p>
        <Form<RebindForm>
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            if (!selected) return;
            setRebindLoading(true);
            try {
              await rebindPrimary(selected.id, {
                candidate: { playerId: values.playerId, uuid: values.uuid, name: values.name },
                reason: values.reason,
                totp: values.totp
              });
              void message.success("主账号已换绑，未来邀请将归入新账号");
              setRebindOpen(false);
              setSelected(null);
              loadDashboard();
            } catch (error) {
              void message.error(error instanceof Error ? error.message : "换绑失败");
            } finally {
              setRebindLoading(false);
            }
          }}
        >
          <Form.Item name="playerId" label="EasyBot Player.Id" rules={[{ required: true }]}>
            <InputNumber min={0} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="uuid" label="新账号 UUID" rules={[{ required: true, min: 32, max: 36 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="新账号名" rules={[{ required: true, max: 64 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="reason" label="换绑原因" rules={[{ required: true, min: 4, max: 500 }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="totp" label="TOTP 动态码" rules={[{ required: true, len: 6 }]}>
            <Input maxLength={6} inputMode="numeric" />
          </Form.Item>
        </Form>
          </Modal>
          <AccountSettings
            open={accountOpen}
            user={user}
            onClose={() => setAccountOpen(false)}
            onUserChange={setUser}
          />
        </Layout.Content>
      </Layout>
    </Layout>
  );
}

export function App() {
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: "#1677ff",
          colorInfo: "#1677ff",
          colorSuccess: "#52c41a",
          colorWarning: "#faad14",
          colorError: "#ff4d4f",
          colorBgLayout: "#f5f5f5",
          colorBgContainer: "#ffffff",
          borderRadius: 6,
          fontFamily: "\"Microsoft YaHei UI\", \"Microsoft YaHei\", \"PingFang SC\", \"Heiti SC\", \"SimHei\", sans-serif",
          fontWeightStrong: 700
        },
        components: {
          Card: { headerBg: "#ffffff" },
          Table: { headerBg: "#fafafa", rowHoverBg: "#f5f8ff" },
          Layout: { bodyBg: "#f5f5f5", headerBg: "#ffffff", siderBg: "#ffffff" }
        }
      }}
    >
      <AntdApp message={{ duration: 4, maxCount: 3 }}>
        <RootApp />
      </AntdApp>
    </ConfigProvider>
  );
}

